import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { CostCenterAggregationDashboardDto } from 'api/dashboard/dtos/CostCenterAggregationDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from '../topMaintenanceCosts/TopMaintenanceCosts.module.scss';
import layoutStyles from '../costCenterExpenses/CostCenterLayout.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a',
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0',
];

const formatHours = (minutes: number): string => {
	const total = Math.max(Math.round(minutes), 0);
	const h = Math.floor(total / 60);
	const m = total % 60;
	return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} h`;
};

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const CostCenterVisits = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<CostCenterAggregationDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('costCenterVisits');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getCostCenterVisits(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo, ...vehicleScope };
				const previous = await DashboardService.getCostCenterVisits(prevCriteria);
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.totalDurationMinutes, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const items = data || [];
	const maxMinutes = Math.max(...items.map(d => d.totalDurationMinutes), 0);
	const grandTotalMinutes = items.reduce((sum, it) => sum + it.totalDurationMinutes, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.cost_center_visits') as string}
			subTitleTooltip={t((scopeVehicleId ? 'dashboard.cost_center_visits_asset_tooltip' : 'dashboard.cost_center_visits_tooltip') as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : items.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={formatHours(grandTotalMinutes)}
						subLabel={t('dashboard.total_duration' as any) as string}
						currentTotal={grandTotalMinutes}
						previousTotal={previousTotal}
						previousRange={previousRange}
						lowerIsBetter={false}
					/>
					<div className={styles.list}>
					<ReactTooltip effect="solid" />
					{visibleItems.map((item, index) => {
						const barWidth = maxMinutes > 0 ? (item.totalDurationMinutes / maxMinutes) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.geofenceId}
								className={styles.row}
								onClick={() => {
									const params = new URLSearchParams({ tab: 'visits-report' });
									if (dateFrom) params.set('dateFrom', dateFrom.toISOString());
									if (dateTo) params.set('dateTo', dateTo.toISOString());
									window.open(`/geofences/details/${item.geofenceId}?${params}`, '_blank');
								}}
							>
								<span className={`${styles.plate} ${layoutStyles.plateWide}`} data-tip={item.name}>{item.name}</span>
								<div className={styles.barTrack}>
									<div
										className={styles.barFill}
										style={{ width: `${barWidth}%`, background: color }}
									/>
								</div>
								<span className={`${styles.cost} ${index === 0 ? styles.costFirst : ''}`}>
									{formatHours(item.totalDurationMinutes)}
								</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
				</>
			)}
		</Box>
	);
};

export default CostCenterVisits;
